import { isInstalledAppMode } from '../analytics/device-context';
import { browserFamily, type BrowserVersionNavigator } from './browser-version-gate';

/**
 * Windows launcher (source: tools/startup-launcher/) that shows a JioPC splash at sign-in
 * and opens the installed PWA behind it, via a Task Scheduler logon task. Replaced
 * JioPCStartup.exe, whose HKCU Run entry left users on an empty desktop for ~30s.
 * Shipped from `public/downloads/` and served by nginx's `/downloads/` block, which
 * forces a download and 404s instead of falling back to index.html.
 *
 * A web page cannot run or install it — the user downloads and runs it themselves.
 * Signing only removes the SmartScreen warning; it does not make the install silent.
 */
export const STARTUP_HELPER_URL = '/downloads/JioPCLauncher.exe';

type StartupHelperNavigator = BrowserVersionNavigator & {
  userAgentData?: BrowserVersionNavigator['userAgentData'] & { platform?: string };
};

function isWindows(nav: StartupHelperNavigator): boolean {
  const platform = nav.userAgentData?.platform;
  return platform ? platform === 'Windows' : /Windows/i.test(nav.userAgent ?? '');
}

/**
 * Offer the helper only where it can work: the installed app (a browser tab has no app
 * shortcut for the helper to register) on Windows, in Chrome or Edge — the launcher
 * reuses whichever browser's shortcut it finds.
 */
export function canOfferStartupHelper(
  nav: StartupHelperNavigator = typeof navigator !== 'undefined' ? navigator : {},
  installed: boolean = isInstalledAppMode(),
): boolean {
  return installed && isStartupHelperBrowser(nav);
}

/** Windows + Chrome/Edge: where the launcher can find a PWA shortcut. */
export function isStartupHelperBrowser(
  nav: StartupHelperNavigator = typeof navigator !== 'undefined' ? navigator : {},
): boolean {
  const family = browserFamily(nav);
  return isWindows(nav) && (family === 'chrome' || family === 'edge');
}

/** Days a "Not now" on the dashboard auto-start card stays quiet before asking again. */
export const STARTUP_HELPER_NOT_NOW_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/** Persisted prompt state: `'downloaded'`, or the epoch-ms of the last "Not now". */
export const STARTUP_HELPER_DOWNLOADED = 'downloaded';

/** Whether the dashboard should show the first-run auto-start card, given the stored state. */
export function shouldPromptStartupHelper(stored: string | null, now: number = Date.now()): boolean {
  if (stored === null) return true;
  if (stored === STARTUP_HELPER_DOWNLOADED) return false;
  const at = Number(stored);
  return !Number.isFinite(at) || now - at >= STARTUP_HELPER_NOT_NOW_COOLDOWN_MS;
}

/**
 * Starts the exe download from a click handler. The browser saves it to the user's
 * download folder — a page cannot pick the path, and the helper does not need one: it
 * locates Chrome via the registry and writes a Run entry pointing at Chrome, not at itself.
 */
export function downloadStartupHelper(): void {
  const link = document.createElement('a');
  link.href = STARTUP_HELPER_URL;
  link.download = 'JioPCLauncher.exe';
  link.click();
}

/** Query parameter the launcher adds to the URL it opens the app with. */
export const LAUNCHER_QUERY_PARAM = 'launcher';

/**
 * How long one launcher-made launch counts as "the launcher is installed". The launcher runs
 * at every Windows sign-in, so a machine that is restarted or signed into at least weekly
 * keeps refreshing it; once the launcher is uninstalled or deleted, its launches stop and the
 * download link comes back after this window.
 */
export const LAUNCHER_ACTIVE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** True when this page load was opened by the launcher (`?launcher=1`). */
export function isLauncherLaunch(search: string): boolean {
  return new URLSearchParams(search).get(LAUNCHER_QUERY_PARAM) === '1';
}

/** Whether a launcher launch was seen recently enough to treat the launcher as installed. */
export function isLauncherActive(seenAt: number | null, now: number = Date.now()): boolean {
  return seenAt !== null && now - seenAt < LAUNCHER_ACTIVE_WINDOW_MS;
}
